import {
  type FilterProvider,
  FilterValueType,
  type AnyBlock,
  type MutableFilterBlock,
  type SuppliedFilterProviderOptions,
  type ValueRowProps,
  type FilterValueData,
  type ARIAFilterSuggestion,
  FilterProviderType,
  type FilterConfig,
  type IndexedBlockValueItem,
  type QueryContext,
  INDETERMINANT,
} from '../types'
import {AsyncFilterProvider} from './async'
import {getFilterValue, getUnescapedFilterValue} from '../utils'
import {FILTER_KEYS} from '../constants/filter-constants'
import {ValueIcon} from '../utils/ValueIcon'
import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'
import type {FilterQuery} from '../filter-query'

const LABEL_SUGGESTIONS_ENDPOINT = '/_filter/labels'
const LABEL_VALIDATION_ENDPOINT = '/_filter/labels/validate'

export type Label = {
  name: string
  nameHtml?: string
  description?: string
  color?: string
}

export class LabelFilterProvider extends AsyncFilterProvider<Label> implements FilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.label, options)
    this.suggestionEndpoint = LABEL_SUGGESTIONS_ENDPOINT
    this.validationEndpoint = LABEL_VALIDATION_ENDPOINT
    this.type = FilterProviderType.Select
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.processSuggestion.bind(this), caretIndex)
  }

  getIconData(label: Label, context: QueryContext) {
    if (!context['repo']) {
      return {icon: this.icon}
    } else {
      const iconColor = label.color?.startsWith('#') ? label.color : `#${label.color}`
      return {iconColor, description: label.description, inlineDescription: false}
    }
  }

  protected processSuggestion(label: Label, query: string, filterQuery: FilterQuery): ARIAFilterSuggestion {
    let priority = 3

    if (query) {
      if (label.name) priority -= fuzzyScore(query, label.name)
    }
    return {
      type: FilterValueType.Value,
      displayName: label.nameHtml ?? label.name,
      ariaLabel: `${label.name}, ${this.displayName}`,
      value: label.name ?? '',
      priority,
      ...this.getIconData(label, filterQuery.context),
    }
  }

  override validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    label: Label | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!extractedValue) return false

    if (label)
      return {
        iconColor: label.color?.startsWith('#') ? label.color : `#${label.color}`,
        value: extractedValue,
        displayName: label.name,
        ...this.getIconData(label, filterQuery.context),
      }

    if (!filterQuery.context.repo && !filterQuery.context.org) {
      return {
        value: INDETERMINANT,
      }
    }

    return false
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'block',
      leadingVisual: ValueIcon({value, providerIcon: this.icon}),
    }
  }
}
